<script setup>
import { ref, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'next/spinner/Spinner.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});
const hasError = ref(false);
const showGallery = ref(false);
const isImageLoaded = ref(false);
const loadRetries = ref(0);
const sourceUrl = ref(props.attachment.dataUrl);

const { filteredCurrentChatAttachments } = useMessageContext();

const handleLoad = () => {
  isImageLoaded.value = true;
};

// A freshly landed image (received, or just sent) can 404 for an instant
// while the file is still settling in storage; <img> never retries on its
// own. Reload with a fresh cache-buster a few times before giving up, same
// pattern already used for audio (see chips/Audio.vue#onLoadError) and the
// full-size bubble (see bubbles/Image.vue#handleError).
const handleError = () => {
  const url = props.attachment.dataUrl;
  if (url && loadRetries.value < 3) {
    loadRetries.value += 1;
    setTimeout(() => {
      sourceUrl.value = timeStampAppendedURL(url);
    }, 1000 * loadRetries.value);
    return;
  }
  hasError.value = true;
};

watch(
  () => props.attachment.dataUrl,
  newUrl => {
    hasError.value = false;
    loadRetries.value = 0;
    isImageLoaded.value = false;
    sourceUrl.value = newUrl;
  }
);
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-md cursor-pointer relative"
    @click="showGallery = true"
  >
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-md size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <template v-else>
      <div
        v-if="!isImageLoaded"
        class="absolute inset-0 flex items-center justify-center bg-n-alpha-1"
      >
        <Spinner :size="16" />
      </div>
      <img
        class="object-cover w-full h-full skip-context-menu"
        :class="{ invisible: !isImageLoaded }"
        :src="sourceUrl"
        @load="handleLoad"
        @error="handleError"
      />
    </template>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
